import { useNavigate } from "@tanstack/react-router";
import { Button, DarkHero, DarkStatTile, EmptyState, Page } from "@cntrl/ui";
import { useBandContextOrNull, useCapability } from "../../app/bandContext.js";
import { useConnections } from "../../app/hooks/useConnections.js";
import { useMarketingHeroPhoto } from "../../app/hooks/useMarketingHeroPhoto.js";
import { useMarketingOverview, type MarketingKpis, type BoostRec } from "../../app/hooks/useMarketing.js";
import { AdsManager } from "./AdsManager.js";
import { RecommendedAds } from "./RecommendedAds.js";
import { ctr, paidEmptySub, pctOrDash } from "./marketingFormat.js";
import styles from "./MarketingScreen.module.scss";

type OverviewQuery = ReturnType<typeof useMarketingOverview>;

/**
 * The dark "Marketing overview" hero — the four paid-performance tiles over the last 30 days, on the shared
 * `DarkHero` + `DarkStatTile` surface.
 *
 * **The four tiles are the reference's, not four convenient metrics**: Paid spend · Paid reach · Impressions ·
 * Clicks / CTR. The first two keep the "Paid" qualifier because this page also talks about ORGANIC reach one
 * section down, and a bare "Reach · 30d" beside "Recommended for an ad" is genuinely ambiguous. The fourth is a
 * COMBINED reading ("6,420 · 1.25%") — a count and the rate it converted at are one question.
 *
 * **Each tile's empty state says WHY.** Not-connected and no-spend are different sentences and must never
 * collapse: one has something for the band to do, the other does not.
 *
 * The press-photo backdrop is whichever slot holds the **Marketing Page** placement (PO, 2026-09-06; ART-95).
 */
function MarketingOverviewHero({
  kpis,
  metaConnected,
  onConnect,
  q,
}: {
  kpis: MarketingKpis | null | undefined;
  metaConnected: boolean;
  onConnect: (() => void) | null;
  q: OverviewQuery;
}) {
  const loading = q.isLoading;
  const photoUrl = useMarketingHeroPhoto();
  const emptySub = paidEmptySub(metaConnected, kpis?.hasAdAccount ?? false);
  const deltas = kpis?.deltas;
  // The rate half of the combined tile. Null when unmeasured, so the tile shows the count alone rather than
  // "6,420 · -", which reads as a broken template.
  const rate = ctr(kpis?.clicks, kpis?.impressions);
  const ctrPct = rate != null ? pctOrDash(rate) : null;

  return (
    <DarkHero
      titleId="mkt-overview-heading"
      title="Marketing overview."
      photoUrl={photoUrl}
      eyebrow={
        <>
          <span className={styles.liveDot} aria-hidden="true" />
          Last 30d
        </>
      }
      action={
        !metaConnected && !loading && onConnect ? (
          // ghost (paper fill), NOT accent — a crimson button on the crimson spotlight glow fails SC 1.4.11 (~1.6:1
          // boundary + an invisible crimson focus ring); the white ghost fill gives a legible boundary + focus ring on dark.
          <Button variant="ghost" size="sm" onClick={onConnect}>
            Connect Meta
          </Button>
        ) : null
      }
    >
      {q.isError ? (
        <div className={styles.overviewError} role="status">
          <span>Couldn&apos;t refresh your paid performance.</span>
          <button type="button" className={styles.overviewRetry} onClick={() => void q.refetch()}>
            Retry
          </button>
        </div>
      ) : null}
      <div className={styles.overviewStats}>
        <DarkStatTile
          label="Paid spend"
          value={kpis?.spendCents != null ? kpis.spendCents / 100 : null}
          prefix="$"
          loading={loading}
          delta={{ value: deltas?.spendPct }}
          emptySub={emptySub}
        />
        <DarkStatTile
          label="Paid reach"
          value={kpis?.reach ?? null}
          loading={loading}
          delta={{ value: deltas?.reachPct }}
          emptySub={emptySub}
        />
        <DarkStatTile
          label="Impressions"
          value={kpis?.impressions ?? null}
          loading={loading}
          delta={{ value: deltas?.impressionsPct }}
          emptySub={emptySub}
        />
        {/* The pair rides ONE tile: `value` keeps the null → '—' contract on the count, `secondary` carries the
            rate. Deliberately no delta — the reference shows none here either, and a CTR movement and a click
            movement are two different arrows that cannot share one slot. */}
        <DarkStatTile
          label="Clicks / CTR"
          value={kpis?.clicks ?? null}
          exact
          secondary={ctrPct}
          loading={loading}
          emptySub={emptySub}
        />
      </div>
    </DarkHero>
  );
}

/**
 * The Marketing tab — the artist-side paid + promo hub, in the reference's order:
 * hero → Recommended for an ad → Meta Ads (KPI strip · campaigns · audience builder).
 *
 * **Nothing else lives here** (operator ruling, 2026-09-09, from the marketing-page parity audit). An "Active
 * release" box, a releases + DSP list, a press-hits feed, a Fans link and a config-driven Google Ads
 * coming-soon card all shipped on this page from the Initial Commit and none is on the page this audits
 * against; Google Ads in particular renders NOTHING in the reference, deliberately, because no connector
 * exists. Press hits keeps its real home in the release command center and Fans keeps its own route and nav
 * entry, so nothing was lost with them.
 *
 * **Every escape from this page is lens-aware.** The screen mounts BOTH at `/marketing` (artist shell) and at
 * `/roster/artists/{id}/marketing` (roster shell), and an artist-shell absolute path leaves the roster shell
 * entirely — landing an org seat on `RequireBand`'s "No band selected". That is exactly how the Booking lens's
 * Assets tab failed, and this page had four of them.
 */
export function MarketingScreen() {
  const canSee = useCapability("releases:read");
  const q = useMarketingOverview();
  const connections = useConnections();
  const navigate = useNavigate();
  const inRoster = useBandContextOrNull()?.shell === "roster";

  if (!canSee) {
    return (
      <Page>
        <EmptyState
          title="Marketing isn't available for your role"
          description="The Marketing tab is available to owner/AV, management, label, and content roles."
        />
      </Page>
    );
  }

  const data = q.data;
  // The status strip prefers the REAL, live connection state (`useConnections` — invalidated by
  // connection-changed, so it repaints the moment Meta connects) once it has loaded; until then it falls back to the
  // overview's authoritative metaConnected flag, so a genuinely-connected band never flashes the "Connect Meta" CTA
  // on a cold load.
  const metaConnected = connections.data
    ? (connections.data.items ?? []).some((c) => c.provider === "meta" && c.connected === true)
    : (data?.kpis?.metaConnected ?? false);

  // Both are artist-shell routes. Null under the roster lens ⇒ the affordance is not rendered at all, rather
  // than rendered and broken (the dead-button rule).
  const openConnections = inRoster ? null : () => void navigate({ to: "/settings/connections" });
  const openAdCreator = inRoster
    ? null
    : (rec?: BoostRec) =>
        void navigate({
          to: "/marketing/campaigns/new",
          // "Boost this" and "+ New campaign" open the SAME creator — one instance by construction, since it is
          // a routed modal. The post seeds the creator's destination so the boost lands somewhere.
          search: rec?.permalink ? { dest: rec.permalink } : undefined,
        });

  return (
    <Page>
      <MarketingOverviewHero kpis={data?.kpis} metaConnected={metaConnected} onConnect={openConnections} q={q} />

      <RecommendedAds onBoost={(rec) => openAdCreator?.(rec)} onConnect={openConnections} />

      <AdsManager onConnect={openConnections} onNewCampaign={openAdCreator ? () => openAdCreator() : null} />
    </Page>
  );
}
